import * as THREE from 'three';
import type { WorldSnapshot } from '../../../shared/network/protocol.js';
import type { CombatVisualEvent } from '../../../shared/game/combat-types.js';
import { shepherdSpells } from '../../../content/spells/effects.js';
import { disposeEffect, effectParticles, shadeEffect } from './effect-surfaces.js';
import { aim, disc, giltMaterial, glowBall, HELD, rayDirections, shown, streak } from './shepherd-effects.js';
import type { SpellLight } from './spell-effects.js';

const RADIANT = shepherdSpells.radiant;
/** How high above its target a Judgment's shaft of light begins, in metres. */
const SKY = 7;
/** Each moment's length in game seconds: Judgment Dice set aside, a Judgment blow, and Lay on Hands. */
const DURATION = { judgment: .8, smite: .95, touch: 1.5 } as const;
type FlashKind = keyof typeof DURATION;
interface Flash {
  kind: FlashKind; unitId: string; casterId?: string; at: number; scale: number; seed: number; position: THREE.Vector3;
  group: THREE.Group; core: THREE.Mesh; ring: THREE.Mesh; pool?: THREE.Mesh; beam?: THREE.Group; motes: THREE.Points;
  rays: { group: THREE.Group; dir: THREE.Vector3; length: number }[]; hands: THREE.Mesh[];
}
/** An Oathsworn holding Judgment Dice: a faint gold skin of light over its weapon until a blow spends them. */
interface Holding { group: THREE.Object3D; skins: THREE.Mesh[]; gilt: THREE.ShaderMaterial; level: number; seed: number }

/**
 * The Oathsworn's Radiant Judgment and Lay on Hands, presentation only. Setting Judgment Dice aside flares gold round the hero, and its
 * weapon keeps a faint gold sheen while it holds them. The blow that spends them brings a shaft of light down on its foe, bursting in rays
 * with a ring on the ground, harder the more the dice rolled. Lay on Hands lights the Oathsworn's hands and raises a warm column through
 * whoever it heals.
 */
export class OathswornEffects {
  private flashes: Flash[] = [];
  private holding = new Map<string, Holding>();
  private lastTime: number | undefined;
  readonly lights: SpellLight[] = [];
  get visualObjects(): THREE.Object3D[] { return [...this.flashes.map(flash => flash.group), ...this.flashes.flatMap(flash => flash.hands)]; }
  constructor(private scene: THREE.Scene, private heights: ReadonlyMap<string, number>) {}

  receive(event: CombatVisualEvent, at: number) {
    if (event.kind === 'ability' && event.abilityId === 'radiant-judgment') this.flash('judgment', event, event.actorId, at);
    else if (event.kind === 'impact' && event.judgment) this.flash('smite', event, event.targetId, at, Math.min(1.4, .75 + event.judgment / 14));
    else if (event.kind === 'heal' && event.abilityId === 'lay-on-hands') this.flash('touch', event, event.targetId, at, 1, event.actorId);
  }

  update(snapshot: WorldSnapshot, earlier: WorldSnapshot, alpha: number, positions: ReadonlyMap<string, THREE.Object3D>, ppm = 40) {
    const time = earlier.gameTime + (snapshot.gameTime - earlier.gameTime) * alpha;
    const dt = THREE.MathUtils.clamp(time - (this.lastTime ?? time), 0, .1); this.lastTime = time;
    this.lights.length = 0;
    this.updateHolding(snapshot, positions, time, dt);
    this.updateFlashes(positions, time, ppm);
  }

  private flash(kind: FlashKind, event: CombatVisualEvent, unitId: string, at: number, scale = 1, casterId?: string) {
    const group = new THREE.Group(); group.name = `oathsworn-${kind}`; group.visible = false;
    const seed = event.id, place = kind === 'judgment' ? event.from : event.to;
    const flash: Flash = { kind, unitId, casterId, at, scale, seed, position: new THREE.Vector3(place.x, 0, place.z), group,
      core: glowBall(RADIANT.glow, kind === 'touch' ? .5 : .34), ring: disc(RADIANT.glow, 1),
      motes: effectParticles(RADIANT.accent, kind === 'smite' ? 18 : kind === 'touch' ? 16 : 10, kind === 'touch' ? .45 : .4, kind === 'judgment' ? 1.1 : 1.7), rays: [], hands: [] };
    if (kind !== 'touch') rayDirections(kind === 'smite' ? 10 : 6, seed).forEach((dir, i) => {
      const ray = streak(RADIANT.glow, RADIANT.core, 1); group.add(ray);
      flash.rays.push({ group: ray, dir, length: (kind === 'smite' ? .95 : .45) * scale * (.75 + (Math.sin(seed * 5.3 + i * 7.1) * .5 + .5) * .5) });
    });
    if (kind === 'smite') { flash.beam = streak(RADIANT.glow, RADIANT.core); group.add(flash.beam); }
    if (kind !== 'judgment') flash.pool = disc(RADIANT.glow, 0);
    if (kind === 'touch') flash.hands = [glowBall(RADIANT.glow, .12), glowBall(RADIANT.glow, .12)].map(halo => { halo.visible = false; this.scene.add(halo); return halo; });
    for (const part of [flash.core, flash.ring, flash.pool, flash.motes]) if (part) group.add(part);
    this.scene.add(group); this.flashes.push(flash);
    while (this.flashes.length > 24) this.dispose(this.flashes.shift()!);
  }

  private updateFlashes(positions: ReadonlyMap<string, THREE.Object3D>, time: number, ppm: number) {
    this.flashes = this.flashes.filter(flash => {
      const p = (time - flash.at) / DURATION[flash.kind];
      if (p >= 1) { this.dispose(flash); return false; }
      if (p < 0) return true;
      const unit = positions.get(flash.unitId), tall = this.heights.get(flash.unitId) ?? 2;
      if (unit) flash.position.copy(unit.position);
      flash.group.position.copy(flash.position); flash.group.visible = true;
      const center = tall * .55, fade = Math.pow(1 - p, 1.5) * THREE.MathUtils.smoothstep(p, 0, .05), out = 1 - Math.pow(1 - Math.min(1, p * 2.6), 3);
      for (const ray of flash.rays) {
        const from = new THREE.Vector3(0, center, 0).addScaledVector(ray.dir, .08), to = new THREE.Vector3(0, center, 0).addScaledVector(ray.dir, .12 + ray.length * out);
        aim(ray.group, from, to, flash.kind === 'smite' ? .2 : .14); shadeEffect(ray.group, time, fade * 1.2);
      }
      const core = flash.core.material as THREE.ShaderMaterial;
      if (flash.kind === 'touch') {
        // A column of warm light rising through the one healed.
        flash.core.position.y = .2 + p * tall * .75; flash.core.scale.set(.8 + p * .4, 1.6 + p * .8, .8 + p * .4);
        core.uniforms.uOpacity.value = fade * 1.1;
      } else {
        flash.core.position.y = center; flash.core.scale.setScalar((.45 + out * (flash.kind === 'smite' ? 1.1 : .8)) * flash.scale);
        core.uniforms.uOpacity.value = Math.pow(1 - p, 2.4) * (flash.kind === 'smite' ? 1.6 : 1.1);
      }
      if (flash.beam) {
        // The shaft falls at once and thins away: brightest where it strikes.
        const width = (.55 - p * .45) * flash.scale;
        aim(flash.beam, new THREE.Vector3(0, SKY, 0), new THREE.Vector3(0, center * .7, 0), Math.max(.02, width));
        shadeEffect(flash.beam, time, Math.pow(1 - Math.min(1, p * 1.6), 2) * 1.4);
      }
      flash.motes.position.y = flash.kind === 'smite' ? center - .3 : .1; shadeEffect(flash.motes, time, fade * 1.1, p, ppm);
      flash.ring.position.y = .04; flash.ring.scale.setScalar((flash.kind === 'judgment' ? .3 + out * .55 : .3 + out * .8) * flash.scale);
      (flash.ring.material as THREE.ShaderMaterial).uniforms.uOpacity.value = fade * (flash.kind === 'judgment' ? .45 : .6);
      if (flash.pool) { flash.pool.position.y = .03; flash.pool.scale.setScalar(.8 * flash.scale); (flash.pool.material as THREE.ShaderMaterial).uniforms.uOpacity.value = fade * .4; }
      // Lay on Hands: the Oathsworn's hands burn gold as the touch lands, and go out first.
      const caster = flash.casterId ? positions.get(flash.casterId) : undefined, hands = caster ? ['hand.L', 'hand.R'].map(name => caster.getObjectByName(name)) : [];
      flash.hands.forEach((halo, i) => {
        const bone = hands[i], glow = Math.max(0, 1 - p * 1.8);
        halo.visible = !!bone && glow > 0; if (!bone || !halo.visible) return;
        bone.getWorldPosition(halo.position); (halo.material as THREE.ShaderMaterial).uniforms.uOpacity.value = glow * 1.4;
      });
      const output = flash.kind === 'smite' ? .3 * flash.scale : flash.kind === 'touch' ? .16 : .12;
      this.lights.push({ position: flash.position.clone().setY(flash.position.y + Math.max(.4, center)), color: new THREE.Color(RADIANT.glow), output: output * fade });
      return true;
    });
  }

  /** A faint, slowly breathing gold over the weapons of every hero holding Judgment Dice; it goes out when they are spent or lost. */
  private updateHolding(snapshot: WorldSnapshot, positions: ReadonlyMap<string, THREE.Object3D>, time: number, dt: number) {
    const holders = new Set(snapshot.heroes.filter(hero => hero.class.judgment?.dice.length && !hero.health.dead).map(hero => hero.id));
    for (const id of holders) { const group = positions.get(id); if (group && !this.holding.has(id)) this.holding.set(id, this.gild(group, id)); }
    for (const [id, state] of this.holding) {
      if (positions.get(id) !== state.group) { this.ungild(id); continue; }
      state.level = holders.has(id) ? Math.min(1, state.level + dt / .3) : state.level - dt / .25;
      if (state.level <= 0) { this.ungild(id); continue; }
      state.gilt.uniforms.uOpacity.value = state.level * (.32 + Math.sin(time * 2.4 + state.seed) * .1);
      const lit = state.skins.find(skin => shown(skin, state.group));
      if (lit) this.lights.push({ position: lit.getWorldPosition(new THREE.Vector3()), color: new THREE.Color(RADIANT.glow), output: .02 * state.level });
    }
  }
  private gild(group: THREE.Object3D, id: string): Holding {
    const gilt = giltMaterial(RADIANT.glow), skins: THREE.Mesh[] = [];
    for (const name of HELD) group.getObjectByName(name)?.traverse(node => {
      if (!(node instanceof THREE.Mesh) || node.name.endsWith('-gilt')) return;
      const skin = new THREE.Mesh(node.geometry, gilt); skin.name = 'judgment-gilt'; skin.renderOrder = 2; node.add(skin); skins.push(skin);
    });
    return { group, skins, gilt, level: 0, seed: id.length * 2.3 };
  }
  private ungild(id: string) {
    const state = this.holding.get(id); if (!state) return;
    for (const skin of state.skins) skin.removeFromParent();
    state.gilt.dispose(); this.holding.delete(id);
  }
  private dispose(flash: Flash) { disposeEffect(flash.group); for (const halo of flash.hands) disposeEffect(halo); }
  clear() {
    for (const flash of this.flashes) this.dispose(flash);
    for (const id of [...this.holding.keys()]) this.ungild(id);
    this.flashes = []; this.lights.length = 0; this.lastTime = undefined;
  }
}
